'use client';

import { FormEvent, useMemo, useState } from "react";
import Link from "next/link";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faLock, faUser, faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { useUser } from "@/context/UserContext";
import { apiFetch, apiRoutes } from "@/lib/api";

type RegisterResponse = {
  message: string;
};

type LoginResponse = {
  token: string;
};

const MIN_PASSWORD_LENGTH = 6;

export function RegisterForm() {
  const { setToken } = useUser();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const disableSubmit = useMemo(() => {
    return (
      !name.trim() ||
      !email.trim() ||
      password.length < MIN_PASSWORD_LENGTH ||
      password !== confirmPassword ||
      !acceptTerms ||
      !acceptPrivacy ||
      loading
    );
  }, [name, email, password, confirmPassword, acceptTerms, acceptPrivacy, loading]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (password !== confirmPassword) {
      setError("As senhas precisam coincidir.");
      return;
    }

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`A senha deve ter pelo menos ${MIN_PASSWORD_LENGTH} caracteres.`);
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await apiFetch<RegisterResponse>(apiRoutes.auth.register(), {
        method: "POST",
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          password,
          confirmPassword,
          acceptTerms,
          acceptPrivacy
        })
      });

      setSuccess(response.message ?? "Cadastro realizado com sucesso!");

      // Realiza login automático após o cadastro para manter a sessão ativa
      const loginResponse = await apiFetch<LoginResponse>(apiRoutes.auth.login(), {
        method: "POST",
        body: JSON.stringify({
          email: email.trim(),
          password
        })
      });

      setToken(loginResponse.token);

      // Pequeno atraso garante persistência do cookie antes da navegação
      await new Promise((resolve) => setTimeout(resolve, 50));
      window.location.href = "/obrigado";
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Não foi possível concluir o cadastro.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
      {/* Nome */}
      <div className="space-y-1.5">
        <label htmlFor="name" className="text-xs font-medium text-white/70">
          Nome completo
        </label>
        <div className="relative">
          <FontAwesomeIcon icon={faUser} className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
          <input
            id="name"
            type="text"
            autoComplete="name"
            required
            value={name}
            onChange={(event) => setName(event.target.value)}
            className="w-full rounded-xl border border-white/10 bg-[#1a1a24] py-3 pl-10 pr-4 text-sm text-white placeholder:text-white/40 transition focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary/50"
            placeholder="Seu nome completo"
          />
        </div>
      </div>

      {/* Email */}
      <div className="space-y-1.5">
        <label htmlFor="email" className="text-xs font-medium text-white/70">
          Email
        </label>
        <div className="relative">
          <FontAwesomeIcon icon={faEnvelope} className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
          <input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            className="w-full rounded-xl border border-white/10 bg-[#1a1a24] py-3 pl-10 pr-4 text-sm text-white placeholder:text-white/40 transition focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary/50"
            placeholder="seu@email.com"
          />
        </div>
      </div>

      {/* Senhas em grid */}
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <label htmlFor="password" className="text-xs font-medium text-white/70">
            Senha
          </label>
          <div className="relative">
            <FontAwesomeIcon icon={faLock} className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className="w-full rounded-xl border border-white/10 bg-[#1a1a24] py-3 pl-10 pr-10 text-sm text-white placeholder:text-white/40 transition focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary/50"
              placeholder="••••••••"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-white/40 transition hover:text-white/70"
              tabIndex={-1}
            >
              <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="space-y-1.5">
          <label htmlFor="confirmPassword" className="text-xs font-medium text-white/70">
            Confirmar
          </label>
          <div className="relative">
            <FontAwesomeIcon icon={faLock} className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
            <input
              id="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              autoComplete="new-password"
              required
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              className="w-full rounded-xl border border-white/10 bg-[#1a1a24] py-3 pl-10 pr-10 text-sm text-white placeholder:text-white/40 transition focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary/50"
              placeholder="••••••••"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-white/40 transition hover:text-white/70"
              tabIndex={-1}
            >
              <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
      <p className="text-[11px] text-white/40">Mínimo de {MIN_PASSWORD_LENGTH} caracteres.</p>

      {/* Termos e Privacidade */}
      <div className="space-y-2.5 rounded-xl border border-white/10 bg-white/5 p-3">
        <label className="flex cursor-pointer items-start gap-2.5">
          <input
            type="checkbox"
            checked={acceptTerms}
            onChange={(e) => setAcceptTerms(e.target.checked)}
            className="mt-0.5 h-4 w-4 flex-shrink-0 cursor-pointer rounded border-white/20 bg-white/10 text-brand-primary focus:ring-brand-primary focus:ring-offset-0"
          />
          <span className="text-xs text-white/70">
            Concordo com os{" "}
            <a href="/termos" target="_blank" rel="noreferrer" className="font-medium text-brand-primary underline hover:text-white">
              Termos de Uso
            </a>
          </span>
        </label>

        <label className="flex cursor-pointer items-start gap-2.5">
          <input
            type="checkbox"
            checked={acceptPrivacy}
            onChange={(e) => setAcceptPrivacy(e.target.checked)}
            className="mt-0.5 h-4 w-4 flex-shrink-0 cursor-pointer rounded border-white/20 bg-white/10 text-brand-primary focus:ring-brand-primary focus:ring-offset-0"
          />
          <span className="text-xs text-white/70">
            Concordo com a{" "}
            <a href="/privacidade" target="_blank" rel="noreferrer" className="font-medium text-brand-primary underline hover:text-white">
              Política de Privacidade
            </a>
          </span>
        </label>
      </div>

      {/* Mensagens de erro/sucesso */}
      {error ? (
        <p role="alert" className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-xs text-red-300">
          {error}
        </p>
      ) : null}

      {success ? (
        <p role="status" className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-xs text-emerald-300">
          {success}
        </p>
      ) : null}

      {/* Botão Cadastrar */}
      <button
        type="submit"
        disabled={disableSubmit}
        className="w-full rounded-full bg-brand-gradient py-3 text-sm font-bold text-white shadow-lg transition-all hover:opacity-90 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-60"
      >
        {loading ? (
          <span className="inline-flex items-center justify-center gap-2">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            Cadastrando...
          </span>
        ) : (
          "Criar conta"
        )}
      </button>

      {/* Link para login */}
      <p className="text-center text-xs text-white/50">
        Já tem uma conta?{" "}
        <Link href="/login" className="font-medium text-brand-primary hover:text-white">
          Entrar
        </Link>
      </p>
    </form>
  );
}
